import React, {useEffect, useState, useMemo} from 'react';
import {
    PlusCircleOutlined,
    TeamOutlined,
    WifiOutlined,
    QrcodeOutlined,
    UnorderedListOutlined,
    ArrowUpOutlined,
    SettingOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {
    Button,
    Space,
    Input,
    Modal,
    Card,
    Typography,
    Row,
    Col,
    Statistic,
    Radio,
    InputNumber,
    Alert,
    Tooltip,
    message,
    Checkbox,
    Skeleton,
    List,
    Table, Tag } from 'antd';
import moment from 'moment';
import firebase from 'firebase/app';

import { ccyIsoCodeToSymbolMap, supportedCurrencies } from '../../utils/CurrencyUtils';
import MoneyTitle from '../customer/MoneyTitle';
import ProductSwitcher from '../../utils/ProductSwitcher';
import TrackerService from '../../utils/TrackerService';
import { useTransferDetails } from '../../models/useTransferDetails';
import { useSessionStorage } from '../../models/useSessionStorage';
import { useTeamMembersTips } from '../../models/useTeamMembersTips';

import DateRangeFilterSelector from './DateRangeFilterSelector';

import '../../App.scss';
import 'antd/dist/antd.css';

const {Paragraph, Text} = Typography;
const {Search} = Input;

// TESTING: Test data to validate datasources
const datasourceForTesting = [
    {
        // REQUIRED fields:
        currency: 'EUR',
        is_test: false,
        product: 'tipping',
        balance: 300, decimalBalance: 3,
        // OPTIONAL fields:
        pending: 100, pendingDecimal: 1,
        available: 200, pendingAvailable: 2,
        withdrawn: 300, withdrawnDecimal: 3,
    },
    {
        currency: 'GBP',
        is_test: false,
        product: 'tipping',
        balance: 400, decimalBalance: 4,
        pending: 200, pendingDecimal: 2,
        available: 0, pendingAvailable: 0,
        withdrawn: 500, withdrawnDecimal: 5,
    },
    {
        currency: 'USD',
        is_test: true,
        product: 'tipping',
        balance: 1400, decimalBalance: 14,
        pending: 200, pendingDecimal: 2,
        available: 200, pendingAvailable: 2,
        withdrawn: 500, withdrawnDecimal: 5,
    }
];

const ccy = ccyIsoCodeToSymbolMap;

export const TeamsTable = ({condition, onAddAccount, locStrings, client, teams, isAdmin, ...rest}) => {

    const footerActionCard = (
        <Space direction='horizontal'>
            <Button key="1" type="text" icon={<PlusCircleOutlined/>} onClick={onAddAccount}>Add a New
                Team</Button> 
        </Space>
    );
    const columns = [
        {
            title: 'Name',
            dataIndex: 'teamName',
            key: 'teamName',
        },
        {
            title: 'Description',
            dataIndex: 'teamDescription',
            key: 'teamDescription'
        },
        {
            title: 'Members',
            dataIndex: 'teamMembers',
            key: 'teamMembers',
        },
        {
            title: '',
            dataIndex: 'teamId',
            key: 'teamId',
            render:
                (teamId, team) => (<>
                        {((team.teamMemberPermissions && team.teamMemberPermissions.isTeamAdmin) || (team.teamMemberRoles && team.teamMemberRoles.includes('admin')))
                        &&
                        <Link to={`./teams/${teamId}`}>
                            <Button type='primary'>Manage Team</Button>
                        </Link>
                        }
                        {((team.teamMemberPermissions && !team.teamMemberPermissions.isTeamAdmin) && (team.teamMemberRoles && !team.teamMemberRoles.includes('admin')))
                        &&
                        <Link to={`./teams/${teamId}`}>
                            <Button type='primary'>View Team</Button>
                        </Link>
                        }</>
                )

        },
    ];

    const tableData = [];
    let key = 1;
    teams.forEach(team => {
        const teamNumberOfDeletedMembers = team && team.teamNumberOfDeletedMembers ? team.teamNumberOfDeletedMembers : 0;
        const teamTotalNumberOfMembers = team.teamNumberOfMembers + teamNumberOfDeletedMembers;
        const teamRow = {
            teamName: team.teamName,
            teamDescription: team.teamDescription,
            teamMembers: isAdmin ? teamTotalNumberOfMembers : team.teamNumberOfMembers,
            teamId: team.teamId,
            teamMemberRoles: team.roles,
            teamMemberPermissions: team.permissions,
            key: key
        }
        key++;
        tableData.push(teamRow);
    });

    return (
        <Table columns={columns} dataSource={tableData} style={{width: '100%'}} pagination={{pageSize: 40}}
               scroll={{y: 300}}/>
    );

};

const LastTransferView = ({ transfer }) => {
    const { toClient, currency, payoutAmountCents, status } = transfer;
    const { displayName } = toClient;
    const formattedDate = moment(transfer.createdAt.toMillis()).format("DD/MM/YYYY HH:mm").toString();
    return (
        <div className="container bg-light my-4 w-75 mw-400 border border-success rounded">
            <div className="text-capitalize pt-2">✅ Successful transfer</div>
            <div className="row">
                <div className="col">
                    <div className="text-left">
                        <div className="row">
                            <div className="col">
                                {displayName}
                            </div>
                        </div>
                        <Tag color='green'>{formattedDate}</Tag>
                    </div>
                </div>
                <div className="col mt-4">
                    <p className="text-right">
                        <MoneyTitle currency={currency} amount={payoutAmountCents} digits={2} />
                        {' ✅'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const TeamPayoutsList = ({condition, locStrings, teamId, membersList, teamDetails, accounts, currentClientId, isAdmin}) => {
    let defaultPayoutValue = 0;

    const [mode, setMode] = useState('list');
    const [showCurrentMembers, setShowCurrentMembers] = useSessionStorage('all-show-current-team-members', true);
    const [showPastMembers, setShowPastMembers] = useSessionStorage('all-show-past-team-members', false);
    const [memberBeingPaid, setMemberBeingPaid] = useState('');
    const [payoutAmount, setPayoutAmount] = useState(defaultPayoutValue);
    const [maxAvailableToWithdraw, setMaxAvailableToWithdraw] = useState(0);
    const [errorAlertMessage, setErrorAlertMessage] = useState(null);
    const [successAlertMessage, setSuccessAlertMessage] = useState(null);
    const [processingPayoutForMember, setProcessingPayoutForMember] = useState(false);
    const [transferId, setTransferId] = useState(null);
    const transfer = useTransferDetails(transferId, 'sent');
    const transferClientId = transfer ? transfer.toClientId : null;

    let teamMainAccount = accounts && accounts.length > 0 ? accounts[0] : null;


    useEffect(() => {
        if (transferClientId) {
            setSuccessAlertMessage('Done!');

            setTimeout(() => {
                setMode('list');
            }, 1000);
        }
    }, [transferClientId]);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render:
                (displayName, member) => (
                    <div>
                        <Space direction="vertical">
                            <Text 
                                strong={(currentClientId === member.clientId)}
                                type={(member.isDeleted) ? 'warning' : null}
                            >
                                {member.clientId === transferClientId &&
                                    <>✅ </>
                                }
                                {displayName}{(currentClientId === member.clientId) && ' (You)'}
                                {(member.isDeleted) && ' [X]'}
                            </Text>
                            { member.lastInwardTransferAt && member.lastInwardTransferAt !== 'N/A' &&
                            <Tag color={"green"} >{member.lastInwardTransferAt}</Tag>
                            }
                            { member.lastInwardTransferAt && member.lastInwardTransferAt === 'N/A' &&
                            <Tag >{'No payout yet'}</Tag>
                            }
                        </Space>
                    </div>
                )
        },
        {
            title: 'To-date',
            dataIndex: 'totalAmountReceivedTillDate',
            key: 'totalAmountReceivedTillDate',
        },
        {
            title: 'Last',
            dataIndex: 'lastPayoutAmount',
            key: 'lastPayoutAmount',
        },
        {
            title: '',
            dataIndex: 'clientId',
            key: 'clientId',
            render:
                (clientId, member) => (
                    <Space wrap={true} align='center'>
                        <Button 
                            disabled={!teamMainAccount} 
                            type='primary' 
                            block={false}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                            onClick={() => showPayoutModal(member)}
                        > 
                            Pay 
                        </Button>
                        { member.totalAmountReceivedTillDate > 0 &&
                        <Link to={`./payouts/${member.clientId}`}>
                            <Button type='secondary'> View </Button> 
                        </Link>
                        }
                    </Space>
                )
        }
    ];

    const tableData = [];
    let key = 1;
    membersList.forEach(member => {
        if (!showPastMembers && member.isDeleted) {
            return;
        }
        if (!showCurrentMembers && !member.isDeleted) {
            return;
        }
        let formattedDate = 'N/A';
        if (member.lastInwardTransferAt) {
            formattedDate = moment(member.lastInwardTransferAt.toMillis()).format("DD/MM/YYYY").toString();
        }
        const memberRow = {
            displayName: member.displayName,
            numberOfWithdrawals: member.numberOfWithdrawals ? member.numberOfWithdrawals : 0,
            totalAmountReceivedTillDate: member.totalAmountReceivedTillDate ? (member.totalAmountReceivedTillDate / 100).toFixed(2) : '-',
            lastInwardTransferAt: formattedDate ? formattedDate : '-',
            lastPayoutAmount: member.lastPayoutAmount ? (member.lastPayoutAmount / 100).toFixed(2) : '-',
            clientId: member.clientId,
            key: member.clientId,
            teamName: member.teamName,
            isDeleted: member.isDeleted,
        }
        key++;
        tableData.push(memberRow);
    });

    let onClickAddMember = () => {
        setMode('add')
    };

    let onClickModalOk = () => {
        setMode('list');
    }

    let onClickModalCancel = () => {
        setMode('list');
        setErrorAlertMessage(null);
        setSuccessAlertMessage(null);
        setPayoutAmount(0);
    }

    let showPayoutModal = (member) => {
        setErrorAlertMessage(null);
        setSuccessAlertMessage(null);
        setTransferId(null);
        setPayoutAmount(defaultPayoutValue);
        setMemberBeingPaid(member);
        setMode('payout-load');
        setProcessingPayoutForMember(false);
    }

    let navigateToMemberPayoutsPage = (member) => {
        setMemberBeingPaid(member);
        setMode('payout-load');
    }

    let onPayoutAmountChange = (number) => {
        setPayoutAmount(number);
    }

    let initiateMemberPayout = async (member) => {
        setErrorAlertMessage(null);
        setSuccessAlertMessage(null);
        if (payoutAmount > teamMainAccount.availableDecimal) {
            setErrorAlertMessage('Payment amount is greater than available balance!');
            return;
        }

        if (payoutAmount <= 0.00) {
            setErrorAlertMessage('Payment amount should be greater than zero!');
            return;
        }

        setProcessingPayoutForMember(true);
        setTransferId(null);
        setSuccessAlertMessage(`Transfer requested...`);

        const isLocalHost = window.location.hostname === 'localhost';
        const isTest = isLocalHost;

        const tracker = new TrackerService({
          isTest,
        });

        const data = {
          teamId,
          teamName: member.teamName,
          memberClientId: member.clientId,
          memberDisplayName: member.displayName,
          numberOfWithdrawals: member.numberOfWithdrawals,
          payoutAmount,
        }

        //callDBWithNewTransferTypeWD()
        const db = firebase.firestore();
        try {
            const newTransfer = await db.collection('transfers').add({
                fromTeamId: teamId,
                fromClientId: teamDetails.teamClientId,
                toClientId: memberBeingPaid.clientId,
                accountId: teamMainAccount.availableAccountId,
                currency: teamMainAccount.currency,
                payoutAmountCents: Math.round(payoutAmount * 100),
                transferType: 'team-to-member'
            });

            const newTransferId = newTransfer ? newTransfer.id : null;
            setTransferId(newTransferId);

            setSuccessAlertMessage(`Transfer being processed...`);

            tracker.mixpanelTrack("transfer_to_team_member", data);
    
            if (!isTest) {
              tracker.googleTrack(
                "transfer_to_team_member", data);
            }
            // setProcessingPayoutForMember(false);
        } catch (error) {
            console.log(JSON.stringify(error));
            setErrorAlertMessage('Your request for transfer could not be placed. Please try again');
            setProcessingPayoutForMember(false);
        }
    }

    let onErrorAlertClose = () => {
        setErrorAlertMessage(null);
    }

    let onSuccessAlertClose = () => {
        setSuccessAlertMessage(null);
    }

    return (
        <div style={{marginLeft: '1%'}}>
            <div style={{textAlign: 'left', marginLeft: '9%', height: '0'}}>
                <Space>
                    <>
                        {
                            (mode === 'payout-load') &&
                            <Modal title="Payout to team member"
                                   visible={mode === 'payout-load'}
                                   onOk={() => onClickModalOk()}
                                   onCancel={() => onClickModalCancel()}
                                   onClose={() => onClickModalCancel()}
                                   footer={null}
                                   bodyStyle={{minHeight: '200px', minWidth: '280px'}}
                                   destroyOnClose={true}
                            >
                                <div style={{textAlign: 'center'}}>
                                    {(errorAlertMessage) && <Alert message={errorAlertMessage} type="error" closable
                                        onClose={() => onErrorAlertClose()} />}
                                    {(successAlertMessage) &&
                                        <Alert message={successAlertMessage} type="success" closable
                                            onClose={() => onSuccessAlertClose()} />}

                                    <Card>
                                        <p>You are about to pay <strong>{memberBeingPaid.displayName}</strong></p>
                                    </Card>
                                    <Card>
                                        <p>Enter Amount</p>
                                        <InputNumber style={{marginBottom: '12px'}}
                                                     defaultValue={0}
                                                     precision={2}
                                                     step={0.01}
                                                     onChange={onPayoutAmountChange}
                                        />
                                        <p>
                                            {'Available balance: '}
                                            <strong>
                                            <MoneyTitle currency={teamMainAccount.currency} amount={teamMainAccount.available} digits={2}/>
                                            </strong>
                                        </p>
                                    </Card>
                                    <Button 
                                        style={{marginTop: '15px'}} 
                                        type={'primary'}
                                        onClick={() => initiateMemberPayout(memberBeingPaid)}
                                        disabled={processingPayoutForMember}
                                    >
                                        Initiate Payment
                                    </Button>
                                </div>
                            </Modal>
                        }
                    </>
                </Space>

            </div>

            { transfer &&
                <LastTransferView transfer={transfer} />
            }
            {   <div className='row'>
                    <div className='col text-left ml-2'>
                        <Checkbox
                            checked={showCurrentMembers}
                            onChange={() => setShowCurrentMembers(!showCurrentMembers)}
                        >
                            Show current members
                        </Checkbox>
                    </div>
                    <div className='col text-left ml-2'>
                        <Checkbox
                            checked={showPastMembers}
                            onChange={() => setShowPastMembers(!showPastMembers)}
                        >
                            Show past members
                        </Checkbox>
                    </div>
                </div>
            }
            <Table columns={columns} dataSource={tableData} style={{width: '100%'}} />
        </div>
    );
};

export const TeamActionsButtonsCard = ({
        visible,
        handler,
        locStrings,
        getCurrencyIcon,
        teamMemberLoggedIn,
        isLoggedInMemberAdmin,
        ...rest
    }) => {
    let routes = [];

    const teamMembersRoute = (isLoggedInMemberAdmin || teamMemberLoggedIn && teamMemberLoggedIn.permissions && teamMemberLoggedIn.permissions.canSeeMembers) ?
        [
            {
                icon: <TeamOutlined/>,
                route: 'members',
                text: 'Team Members'
            }
        ]
        : [];

    const teamPayoutsRoute = isLoggedInMemberAdmin || (teamMemberLoggedIn && teamMemberLoggedIn.permissions && teamMemberLoggedIn.permissions.canSeePayouts) ?
        [
            {
                icon: getCurrencyIcon(),
                route: 'payouts',
                text: 'Payouts'
            }
        ]
        : [];

    const teamSettingsRoute = isLoggedInMemberAdmin ?
        [
            {
                icon: <SettingOutlined />,
                route: 'settings',
                text: 'Team Settings'
            }
        ]
        : [];

    const individualsTxnsRoute = isLoggedInMemberAdmin ?
        [
            {
                icon: <UnorderedListOutlined />,
                route: 'member-tips',
                text: "Tip History",
            }
        ]
        : [];

    routes = [
        {
            icon: <QrcodeOutlined />,
            route: 'qrcode',
            text: 'Show Team QR'
        },
        ...teamMembersRoute,
        ...teamPayoutsRoute,
        ...individualsTxnsRoute,
        {
            icon: <WifiOutlined rotate='90' />,
            route: 'tags',
            text: 'All Claimed QRs'
        },
        ...teamSettingsRoute,
    ];

    return visible && (
        <Space>
            <Card align='center' style={{border: 'none'}}>
                <Paragraph>Select an Option</Paragraph>
                <Space direction='vertical' width={'100%'}>
                    {
                        routes.map((v, i) => (
                            <Button
                                key={v.route}
                                icon={v.icon}
                                onClick={() => (handler ? handler(v) : null)}
                                type={(i) ? "secondary" : "primary"}
                                style={{ textAlign: 'start', width: `100%` }}
                            >{v.text}</Button>
                        ))
                    }
                </Space>
            </Card>
        </Space>
    );
};

export const TeamQRCodeImage = ({teamDetails, qrCodeDocument}) => {

    return teamDetails && (
        <Space direction={'vertical'} align={'center'}>

        </Space>
    );
};

export const TeamAccountBalancesView = ({
                                            condition,
                                            locStrings,
                                            client,
                                            clientId,
                                            payouts,
                                            withdrawals,
                                            accounts,
                                            wide,
                                            noButtons,
                                            ...rest
                                        }) => {
    const contentStyle = {
        height: '200px',
        color: '#fff',
        lineHeight: '200px',
        textAlign: 'center',
        background: 'green',
    };


    let element = null;

    const testing = false;
    let datasource = (testing ? datasourceForTesting : accounts) || [];

    const productSwitcher = new ProductSwitcher({locStrings});

    // Filter out accounts with a balance of 0
    //datasource = datasource.filter(account => !!account.total);

    const gridStyle = {
        width: '240px',
        textAlign: 'center',
        padding: 0,
    };

    const cardStyle = {
        textAlign: 'center',
        width: {xs: '100%', sm: '240px', md: '480px', lg: '100%'},
        minWidth: '240px',
        marginLeft: 'auto',
        marginRight: 'auto'
    };

    const rowColAccountElement =
        (<div className="accounts-balances-card" style={{width: '100%', textAlign: 'center'}}>
            <Row justify='center' style={{paddingBottom: '10px'}}>
                {datasource.map((v, i) => (
                    <Col key={i} xs={{span: 24}} sm={{span: 12}} md={{span: 8}} lg={{span: 6}} xl={{span: 6}}
                         xxl={{span: 6}}>
                        <Row gutter={16} key={'row-' + i}>
                            <Col span={0}/>
                            <Col span={24}>
                                <Card style={{textAlign: 'center', padding: '20px', margin: 0}}>
                                    <Statistic
                                        title={`Available ${productSwitcher.getProductMap(v.product).pluralName} (${v.currency})`}
                                        value={((v.available || 0)/100).toFixed(2)}
                                        precision={2}
                                        valueStyle={{color: '#3f8600'}}
                                        prefix={ccy[v.currency]}
                                    />
                                    {(v.pending != null) &&
                                    <div style={{width: '100%'}}>
                                        <Space direction='vertical'>
                                            <Space align='baseline' direction='horizontal'>
                                                <p style={{fontWeight: 'bolder'}}>Pending:</p>
                                                <MoneyTitle amount={v.pending} currency={v.currency} digits={2}/>
                                            </Space>
                                            <Space align='baseline' direction='horizontal'>
                                                <p style={{fontWeight: 'bold'}}>To-date:</p>
                                                <MoneyTitle amount={v.total} currency={v.currency} digits={2}/>
                                            </Space>
                                        </Space>
                                    </div>
                                    }
                                </Card>
                            </Col>
                            <Col span={0}/>
                        </Row>
                    </Col>
                ))
                }
            </Row>
        </div>);

    if (datasource.length > 0) {
        element = rowColAccountElement;
    } else {
        element = null;
    }
    return condition() ? element : null;
};

export const IndividualvsClaimSelector = ({
                                              clientTeams,
                                              client,
                                              setSelectedOption,
                                              claimTypeSelected,
                                              teamIdSelected
                                          }) => {
    
    let onClaimTypeChange = (e) => { 
      let option = e.target.value;
      setSelectedOption({ option });
    }

    let onTeamSelection = (e) => {
        let selectedTeamIdValue = e.target.value;
        let selectedTeamNameValue = e.target['data-teamName'];
        setSelectedOption({option: 'team', teamIdSelected: selectedTeamIdValue, teamNameSelected: selectedTeamNameValue});
    }

    let teamListDisplay = () => {
      return (claimTypeSelected === 'team') ? 'block' : 'none';
    }

    const contentStyle = {
        height: '200px',
        color: '#fff',
        lineHeight: '200px',
        textAlign: 'center',
        background: 'green',
    };

    return <div style={{textAlign: 'center'}}>
        <h3>Claiming Tag for?</h3>
        <Radio.Group 
          onChange={onClaimTypeChange} 
          value={claimTypeSelected}
          style={{marginTop: '20px', marginBottom: '25px'}}>
            <Radio value={'individual'}>Yourself</Radio>
            <Radio value={'team'}>Your team</Radio>
        </Radio.Group>

        {
            <div style={{ width: '100%', textAlign: 'center', display: teamListDisplay(), marginBottom: '20px'}}>
              {clientTeams && clientTeams.length > 0 &&
                <>
                  <h4>Select team</h4>
                  <Radio.Group 
                    onChange={onTeamSelection} 
                    value={teamIdSelected}
                    buttonStyle={'solid'} 
                    buttonColor={'green'}
                    size={'middle'}
                    >
                      <Space direction={'vertical'}>
                        {clientTeams.map((team) => {
                          return <Radio.Button 
                            value={team.teamId}
                            name={team.teamName}
                            key={team.teamId}
                            style={{ width: '100%'}}
                            >
                              {`${team.teamName}`}
                          </Radio.Button>;
                        })}
                      </Space>
                  </Radio.Group>
                </>
              }
            </div>
        }

    </div>;
};

export const MemberTransfersTable = ({condition, locStrings, client, inwardTransfers, ...rest}) => {

    //if( !condition() ) return null;

    const statusColor = {
        'sent': 'green',
        'ready': 'yellow',
        'failed': 'red'
    };

    const dataSource = [];

    const productSwitcher = new ProductSwitcher({locStrings});

    inwardTransfers.map(
        (transferObject, i) => {
            let transfer = transferObject.transfer;
            const formattedDate = moment(transfer.createdAt.toMillis()).format("DD/MM/YYYY").toString();
            const productMap = productSwitcher.getProductMap(transfer.product);
            dataSource.push(
                {
                    key: `transfer-${i}`,
                    date: formattedDate,
                    currency: transfer.currency,
                    value: {currency: transfer.currency, amount: +(transfer.payoutAmountCents)},
                    balance: 0,
                    status: transfer.status,
                    transferUid: transferObject.id
                }
            );
        });


    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render:
                status => (
                    <>
                        <Tag color={statusColor[status]}>{status}</Tag>
                    </>
                )
        },
        {
            title: 'Amount',
            dataIndex: 'value',
            align: 'right',
            render:
                value => (
                    <div>
                        <MoneyTitle amount={value.amount} currency={value.currency} digits={2}/>
                    </div>
                )
        },
    ];

    return (
        <Table
            dataSource={dataSource}
            columns={columns}
        />);

};

export const TeamMembersTipsList = ({condition, locStrings, teamId, teamDetails, membersList, currentClientId, isAdmin}) => {
    const [dateRange, setDateRange] = useState(null);
    const [showCurrentMembers, setShowCurrentMembers] = useSessionStorage('tips-show-current-team-members', true);
    const [showPastMembers, setShowPastMembers] = useSessionStorage('tips-show-past-team-members', false);
    const [pageSize, setPageSize] = useState(10);

    // Filter members based on current/past selection
    const filteredMembers = useMemo(() => {
        return membersList.filter(member => {
            if (showCurrentMembers && !member.isDeleted) return true;
            if (showPastMembers && member.isDeleted) return true;
            return false;
        });
    }, [membersList, showCurrentMembers, showPastMembers]);

    // Use the custom hook for tips data - returns array of members with tips data
    const [membersWithTips, totalPoolAmount, totalRefunded, loading, error] = useTeamMembersTips(teamId, filteredMembers, dateRange);

    // Calculate totals to determine if we should show paid out column
    const totalTips = membersWithTips.reduce((sum, member) => sum + (member.tips || 0), 0);
    const totalPayouts = membersWithTips.reduce((sum, member) => sum + (member.payouts || 0), 0);
    const grandTotal = totalTips + totalPayouts;
    
    // Check if team is members only (no pooling)
    const isMembersOnly = teamDetails?.teamTapBehaviour === 'members';
    
    // Show paid out column only if there are payouts AND team is not members only
    const shouldShowPaidOutColumn = totalPayouts > 0 && !isMembersOnly;

    const columns = [
        {
            title: 'Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (displayName, member) => (
                <div>
                    <Space direction="vertical">
                        <Text 
                            strong={(currentClientId === member.clientId)}
                            type={(member.isDeleted) ? 'warning' : null}
                        >
                            {displayName}{(currentClientId === member.clientId) && ' (You)'}
                            {(member.isDeleted) && ' [Past Member]'}
                        </Text>
                        {member.staffId && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {member.staffId}
                            </Text>
                        )}
                    </Space>
                </div>
            )
        },
        {
            title: 'Tips (Net)',
            dataIndex: 'tipsNet',
            key: 'tipsNet',
            align: 'right',
            render: (_, member) => {
                const tipsAmount = member.tips || 0;
                const currency = teamDetails?.defaultCurrency || 'EUR';
                const formattedAmount = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(tipsAmount / 100);
                
                return (
                    <span style={{ fontWeight: '500' }}>
                        {formattedAmount}
                    </span>
                );
            }
        },
        // Conditionally show Paid Out column
        ...(shouldShowPaidOutColumn ? [{
            title: 'Paid Out',
            dataIndex: 'paidOut',
            key: 'paidOut',
            align: 'right',
            render: (_, member) => {
                const payoutsAmount = member.payouts || 0;
                const currency = teamDetails?.defaultCurrency || 'EUR';
                const formattedAmount = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(payoutsAmount / 100);
                
                return (
                    <span style={{ fontWeight: '500' }}>
                        {formattedAmount}
                    </span>
                );
            }
        }] : []),
        // Add Refunded column if there are refunds
        ...(totalRefunded > 0 ? [{
            title: 'Refunded',
            dataIndex: 'refunded',
            key: 'refunded',
            align: 'right',
            render: (_, member) => {
                const refundedAmount = member.refunded || 0;
                if (refundedAmount === 0) {
                    return <span>-</span>;
                }
                const currency = teamDetails?.defaultCurrency || 'EUR';
                const formattedAmount = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(refundedAmount / 100);
                
                return (
                    <span style={{ 
                        fontWeight: '500',
                        color: '#ff4d4f'
                    }}>
                        ({formattedAmount})
                    </span>
                );
            }
        }] : []),
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            render: (_, member) => {
                const totalAmount = member.total || 0;
                const currency = teamDetails?.defaultCurrency || 'EUR';
                const formattedAmount = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(totalAmount / 100);
                
                return (
                    <span style={{ fontWeight: 'bold' }}>
                        {formattedAmount}
                    </span>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, member) => (
                <Tag color={member.isDeleted ? 'red' : 'green'}>
                    {member.isDeleted ? 'past' : 'current'}
                </Tag>
            )
        }
    ];

    const dataSource = membersWithTips.map(member => ({
        key: member.clientId,
        ...member,
        tipsEarned: member.tips || 0
    }));

    // CSV Download functionality
    const downloadCSV = () => {
        if (!dateRange || filteredMembers.length === 0) {
            return;
        }

        const csvHeaders = [
            'Client ID',
            'Staff ID',
            'Name', 
            'Status',
            'Tips (Net)',
            ...(shouldShowPaidOutColumn ? ['Paid Out'] : []),
            ...(totalRefunded > 0 ? ['Refunded'] : []),
            'Total'
        ];

        const csvData = membersWithTips.map(member => {
            const tipsAmount = member.tips || 0;
            const payoutsAmount = member.payouts || 0;
            const refundedAmount = member.refunded || 0;
            const totalAmount = member.total || 0;
            
            const baseRow = [
                member.clientId,
                member.staffId || '',
                member.displayName,
                member.isDeleted ? 'past' : 'current',
                (tipsAmount / 100).toFixed(2)
            ];
            
            if (shouldShowPaidOutColumn) {
                baseRow.push((payoutsAmount / 100).toFixed(2));
            }
            
            if (totalRefunded > 0) {
                baseRow.push((refundedAmount / 100).toFixed(2));
            }
            
            baseRow.push((totalAmount / 100).toFixed(2));
            return baseRow;
        });

        // Add summary row
        const summaryRow = ['', '', 'ALL TIPS TOTAL', ''];
        summaryRow.push((totalTips / 100).toFixed(2));
        
        if (shouldShowPaidOutColumn) {
            summaryRow.push((totalPayouts / 100).toFixed(2));
        }
        
        if (totalRefunded > 0) {
            summaryRow.push((totalRefunded / 100).toFixed(2));
        }
        
        summaryRow.push((grandTotal / 100).toFixed(2));
        csvData.push(summaryRow);

        // Create CSV content
        const csvContent = [
            csvHeaders.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with date range
        const startDate = dateRange.startDateUTC.toISOString().split('T')[0];
        const endDate = dateRange.endDateUTC.toISOString().split('T')[0];
        const filename = `team-members-tips-${teamId}-${startDate}-to-${endDate}.csv`;
        
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{marginLeft: '1%', marginRight: '1%'}}>
            {/* Time Filter */}
            <div style={{marginBottom: '16px', textAlign: 'left'}}>
                <DateRangeFilterSelector 
                    storageId={`team-tips-${teamId}`}
                    isAdmin={isAdmin}
                    type="team"
                    onSelectRange={setDateRange}
                    disabled={loading}
                />
            </div>

            {/* Error Display */}
            {error && (
                <Alert
                    message="Error loading tips data"
                    description={error}
                    type="error"
                    style={{marginBottom: '16px'}}
                />
            )}

            {/* Summary Card */}
            {dateRange && (
                <Card style={{marginBottom: '16px'}}>
                    <Row gutter={[16, 16]}>
                        {shouldShowPaidOutColumn ? (
                            // Show statistics when both tips and payouts exist
                            <>
                                <Col xs={12} sm={6} md={6} lg={6}>
                                    <Statistic
                                        title="Tips Total"
                                        value={totalTips / 100}
                                        precision={2}
                                        valueStyle={{ color: '#1890ff' }}
                                        formatter={(value) => {
                                            const currency = teamDetails?.defaultCurrency || 'EUR';
                                            return new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(value);
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={6}>
                                    <Statistic
                                        title="Paid Out Total"
                                        value={totalPayouts / 100}
                                        precision={2}
                                        valueStyle={{ color: '#ff7a00' }}
                                        formatter={(value) => {
                                            const currency = teamDetails?.defaultCurrency || 'EUR';
                                            return new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(value);
                                        }}
                                    />
                                </Col>
                                {totalRefunded > 0 && (
                                    <Col xs={12} sm={6} md={6} lg={6}>
                                        <Statistic
                                            title="Total Refunded"
                                            value={totalRefunded / 100}
                                            precision={2}
                                            valueStyle={{ color: '#ff4d4f' }}
                                            formatter={(value) => {
                                                const currency = teamDetails?.defaultCurrency || 'EUR';
                                                return new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: currency,
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                }).format(value);
                                            }}
                                        />
                                    </Col>
                                )}
                                <Col xs={12} sm={6} md={6} lg={6}>
                                    <Statistic
                                        title="All Tips Total"
                                        value={grandTotal / 100}
                                        precision={2}
                                        valueStyle={{ color: '#3f8600' }}
                                        formatter={(value) => {
                                            const currency = teamDetails?.defaultCurrency || 'EUR';
                                            return new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(value);
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={6}>
                                    <Statistic
                                        title="Active Members"
                                        value={filteredMembers.filter(m => !m.isDeleted).length}
                                        valueStyle={{ color: '#722ed1' }}
                                    />
                                </Col>
                            </>
                        ) : (
                            // Show statistics when only tips exist
                            <>
                                <Col xs={12} sm={12} md={6} lg={6}>
                                    <Statistic
                                        title="All Tips Total"
                                        value={totalPoolAmount / 100}
                                        precision={2}
                                        valueStyle={{ color: '#3f8600' }}
                                        formatter={(value) => {
                                            const currency = teamDetails?.defaultCurrency || 'EUR';
                                            return new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: currency,
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(value);
                                        }}
                                    />
                                </Col>
                                {totalRefunded > 0 && (
                                    <Col xs={12} sm={12} md={6} lg={6}>
                                        <Statistic
                                            title="Total Refunded"
                                            value={totalRefunded / 100}
                                            precision={2}
                                            valueStyle={{ color: '#ff4d4f' }}
                                            formatter={(value) => {
                                                const currency = teamDetails?.defaultCurrency || 'EUR';
                                                return new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: currency,
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                }).format(value);
                                            }}
                                        />
                                    </Col>
                                )}
                                <Col xs={12} sm={12} md={6} lg={6}>
                                    <Statistic
                                        title="Active Members"
                                        value={filteredMembers.filter(m => !m.isDeleted).length}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                            </>
                        )}
                    </Row>
                </Card>
            )}

            {/* Member Filters and Download */}
            <div style={{marginBottom: '16px', textAlign: 'left'}}>
                <Space>
                    <Checkbox 
                        checked={showCurrentMembers}
                        onChange={(e) => setShowCurrentMembers(e.target.checked)}
                    >
                        Show current members
                    </Checkbox>
                    <Checkbox 
                        checked={showPastMembers}
                        onChange={(e) => setShowPastMembers(e.target.checked)}
                    >
                        Show past members
                    </Checkbox>
                    {dateRange && filteredMembers.length > 0 && (
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />}
                            onClick={downloadCSV}
                            disabled={loading}
                        >
                            Download
                        </Button>
                    )}
                </Space>
            </div>

            {/* Members Table */}
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={loading}
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onShowSizeChange: (current, size) => {
                        setPageSize(size);
                    },
                }}
                locale={{
                    emptyText: dateRange ? 'No tips found for selected period' : 'Select a time period to view tips'
                }}
            />
        </div>
    );
};

